import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { GraduationCap, ArrowRight, CheckCircle, Users, Books, ChartLineUp } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { BRAND_COPYRIGHT, BRAND_NAME } from '../brand';

const features = [
  {
    icon: Books,
    title: 'Course Management',
    description: 'Create structured courses with chapters, lessons, videos, PDFs, and SCORM content.'
  },
  {
    icon: Users,
    title: 'Multi-Tenant',
    description: 'Isolated environments for each organization with custom branding and settings.'
  },
  {
    icon: ChartLineUp,
    title: 'Progress Tracking',
    description: 'Detailed analytics on learner progress, completion rates, and assessments.'
  }
];

const benefits = [
  'Role-based access control',
  'Assessment & quiz engine',
  'Certificate generation',
  'Detailed reporting',
  'File storage integration',
  'Real-time progress updates'
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background noise-overlay">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-primary-foreground" weight="duotone" />
            </div>
            <span className="font-heading font-bold text-xl">{BRAND_NAME}</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" data-testid="login-btn">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button data-testid="get-started-btn">Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="label-overline mb-4">Enterprise Learning Platform</p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Empower Your Team with
                <span className="text-primary"> Modern Learning</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg">
                A production-ready SaaS Learning Management System with multi-tenant architecture. 
                Create, manage, and deliver courses at scale.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button size="lg" className="gap-2" data-testid="hero-cta">
                    Start Free Trial
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="outline" data-testid="hero-demo">
                    View Demo
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-border shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1758691736493-aa6d22c0f8a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjB0ZWFtJTIwdHJhaW5pbmd8ZW58MHx8fHwxNzc0MzQ1MzAwfDA&ixlib=rb-4.1.0&q=85&w=800"
                  alt="Corporate team training"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-card rounded-xl p-4 border border-border shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                    <Users className="w-6 h-6 text-accent" weight="duotone" />
                  </div>
                  <div>
                    <p className="font-heading font-semibold">10,000+</p>
                    <p className="text-sm text-muted-foreground">Active Learners</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="label-overline mb-4">Features</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Everything You Need
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Powerful features designed for modern corporate training and education.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-card rounded-xl p-8 border border-border card-hover"
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-primary" weight="duotone" />
                </div>
                <h3 className="font-heading text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="label-overline mb-4">Why Choose Us</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">
                Built for Scale, Designed for Growth
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Our platform provides enterprise-grade features with a modern, intuitive interface.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" weight="fill" />
                    <span className="text-sm font-medium">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden border border-border">
                <img
                  src="https://images.unsplash.com/photo-1758872014929-174e4ccdf01f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjBzdHVkeWluZyUyMGxhcHRvcCUyMGNhbG18ZW58MHx8fHwxNzc0MzQ1MzAxfDA&ixlib=rb-4.1.0&q=85&w=600"
                  alt="Person studying"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">
            Ready to Transform Your Training?
          </h2>
          <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
            Join thousands of organizations using {BRAND_NAME} to deliver impactful learning experiences.
          </p>
          <Link to="/register">
            <Button size="lg" variant="secondary" className="gap-2" data-testid="cta-start">
              Get Started Free
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-primary-foreground" weight="duotone" />
              </div>
              <span className="font-heading font-semibold">{BRAND_NAME}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {BRAND_COPYRIGHT}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
